import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap, of, mergeMap } from "rxjs";
import * as SalesActions from "./sales.actions";
import { SalesService } from "app/modules/transactions/sales/sales.service";
import { BatchService } from "app/modules/masters/batch/batch.service";
import { SalesItem } from "app/modules/transactions/sales/models/sales-item.model";

@Injectable()
export class SalesEffects {
  constructor(
    private actions$: Actions,
    private salesService: SalesService,
    private itemBatchService: BatchService
  ) {}

  // Helper method to format expiry date to MM/YY format
  private formatExpiryDate(dateString: string | Date | null | undefined): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      
      return `${month}/${year}`;
    } catch (error) {
      console.error('Error formatting expiry date:', error);
      return '';
    }
  }

  // 🔹 1. FIFO Split Effect
  getFifoBatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.getFifoSplitBatches),
      switchMap(({ itemId, quantity, rowIndex }) =>
        this.itemBatchService.getFifoBatches(itemId, quantity).pipe(
          map((fifoData) => {
            const splitItems: SalesItem[] = fifoData.map((batch) => {
              const quantity = batch.usedQty;
              const rate = batch.rate;
              const amount = quantity * rate;
              return {
                itemId,
                itemName: batch.itemName,
                itemCode: batch.itemCode,
                batchId: batch.batchId,
                quantity,
                expiryDate: this.formatExpiryDate(batch.expiryDate),
                rate,
                gstPercent: batch.gstPercent,
                mrp: batch.mrp,
                discountPercent: 0,
                cgstAmount: 0,
                sgstAmount: 0,
                totalAmount: 0,
                amount,
                batchNo: batch.batchNo,
                unitConversion: batch.unitConversion ?? 1,
              };
            });

            return SalesActions.getFifoSplitBatchesSuccess({
              rowIndex,
              splitItems,
            });
          }),
          catchError((err) =>
            of(
              SalesActions.getFifoSplitBatchesFailure({
                error: "Failed to fetch FIFO batches",
              })
            )
          )
        )
      )
    )
  );

  // 🔹 2. Submit Sale Effect (if needed)
  createSales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.createSales),
      mergeMap((action) =>
        this.salesService.createSalesInvoice(action.sale).pipe(
          map((invoice) => SalesActions.createSalesSuccess({ invoice })),
          catchError(() =>
            of(
              SalesActions.createSalesFailure({
                error: "Failed to create sale",
              })
            )
          )
        )
      )
    )
  );

  searchItems$ = createEffect(() =>
  this.actions$.pipe(
    ofType(SalesActions.searchSalesItems),
    mergeMap((action) =>
      this.salesService
        .searchSalesItems(
          action.query,
          action.searchType,
          action.skip,
          action.take
        )
        .pipe(
          map((res) =>
            SalesActions.searchSalesItemsSuccess({
              items: res.items,
              rowIndex: action.rowIndex,
            })
          ),
          catchError((error) =>
            of(SalesActions.searchSalesItemsFailure({ error }))
          )
        )
    )
  )
);

}
